import { ReactNode, createContext, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { LSKeys, LocalStorage } from '@core/localStorage';
import { MOCK_USER, User } from '@models/user.model';

export interface AuthProviderProps {
  userData: User | null;
  children?: ReactNode;
}

export interface UserContextState {
  isAuthenticated: boolean;
  isLoading: boolean;
  id?: string;
}

export const UserStateContext = createContext<UserContextState>(
  {} as UserContextState
);

export const useUserContext = (): UserContextState => {
  return useContext(UserStateContext);
};

export interface AuthContextModel {
  userAccount: User | null;
  signIn: (email: string, password: string) => Promise<User | null>;
  logOut: () => void;
  MOCK_EMAIL: string;
  MOCK_PASSWORD: string;
}

const MOCK_EMAIL = 'john.doe@gmail.com';
const MOCK_PASSWORD = 'my-password';

export const AuthContext = createContext<AuthContextModel>(
  {} as AuthContextModel
);

export function useAuth(): AuthContextModel {
  return useContext(AuthContext);
}

export const AuthProvider = ({
  userData,
  children,
}: AuthProviderProps): JSX.Element => {
  const [user, setUser] = useState<User | null>(userData);
  const navigate = useNavigate();

  const signIn = (email: string, password: string): Promise<User | null> => {
    const user =
      email === MOCK_EMAIL && password === MOCK_PASSWORD ? MOCK_USER : null;
    setUser(user);
    LocalStorage.set(LSKeys.USER_LOGGED_IN, true);
    return Promise.resolve(user);
  };

  const logOut = () => {
    setUser(null);
    LocalStorage.clear(LSKeys.USER_LOGGED_IN);
    navigate('/', { replace: true });
  };

  const values = useMemo(
    () => ({
      userAccount: user,
      signIn,
      logOut,
      MOCK_EMAIL,
      MOCK_PASSWORD,
    }),
    [user]
  );

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>;
};
